import React, { useEffect, useState } from "react";
import IncludeSideBar from "../../Components/Sidebar/IncludeSideBar";
import "./DataExports.css";

const DataExports = () => {
  const dataExportsApis = require("../../apis/dataExports");
  
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  const getDataExports = async (page = 1, limit = 10) => {
    setLoading(true);
    setError(null);
    try {
      const response = await dataExportsApis.getAllDataExports(page, limit);
      setExports(response.exports || []);
      setPagination(response.pagination || {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1,
        hasNext: false,
        hasPrev: false,
      });
    } catch (error) {
      console.error("Error fetching data exports:", error);
      setError("Failed to load data exports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDataExports(1, pagination.limit);
  }, []);

  const handleDownload = (exportItem) => {
    if (exportItem.status === "completed" && exportItem.url) {
      window.open(exportItem.url, "_blank");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this data export?")) {
      try {
        await dataExportsApis.deleteDataExport(id);
        getDataExports(pagination.page, pagination.limit);
      } catch (error) {
        console.error("Error deleting data export:", error);
        alert("Failed to delete data export");
      }
    }
  };

  const handleApprove = async (id) => {
    try {
      const response = await dataExportsApis.approveDataExport(id);
      alert(response.message || "Data export approved successfully");
      getDataExports(pagination.page, pagination.limit);
    } catch (error) {
      console.error("Error approving data export:", error);
      alert("Failed to approve data export");
    }
  };

  const handleReject = async (id) => {
    const reason = prompt("Please provide a reason for rejection (optional):");
    try {
      const response = await dataExportsApis.rejectDataExport(id, reason || "");
      alert(response.message || "Data export rejected successfully");
      getDataExports(pagination.page, pagination.limit);
    } catch (error) {
      console.error("Error rejecting data export:", error);
      alert("Failed to reject data export");
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      getDataExports(newPage, pagination.limit);
    }
  };

  const handleFirst = () => {
    handlePageChange(1);
  };

  const handleLast = () => {
    handlePageChange(pagination.totalPages);
  };

  const handlePrevious = () => {
    if (pagination.page > 1) {
      handlePageChange(pagination.page - 1);
    }
  };

  const handleNext = () => {
    if (pagination.page < pagination.totalPages) {
      handlePageChange(pagination.page + 1);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { className: "status-badge pending", label: "Pending" },
      processing: { className: "status-badge processing", label: "Processing" },
      completed: { className: "status-badge completed", label: "Completed" },
      rejected: { className: "status-badge rejected", label: "Rejected" },
    };
    const config = statusConfig[status] || { className: "status-badge", label: status };
    return <span className={config.className}>{config.label}</span>;
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getDataTypesBadges = (dataTypes) => {
    if (!dataTypes || dataTypes.length === 0) return <span className="no-data-types">None</span>;
    return dataTypes.map((type, index) => (
      <span key={index} className="data-type-badge">{type}</span>
    ));
  };

  return (
    <IncludeSideBar>
      <div className="data-exports-container">
        <div className="header-section">
          <h2>Data Exports</h2>
          <p>Manage user data export requests and download completed exports</p>
        </div>

        {loading ? (
          <div className="loading">Loading data exports...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : exports.length === 0 ? (
          <div className="no-data">No data exports found</div>
        ) : (
          <div className="table-container">
            <table className="data-exports-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Status</th>
                  <th>Data Types</th>
                  <th>Created At</th>
                  <th>Expires At</th>
                  <th>Download</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {exports.map((exportItem) => (
                  <tr key={exportItem.id}>
                    <td>{exportItem.id}</td>
                    <td>
                      <div className="user-info">
                        <img
                          src={exportItem.user?.profile_pic || "/default-avatar.png"}
                          alt={exportItem.user?.nickname || "User"}
                          className="user-avatar"
                        />
                        <div className="user-details">
                          <div className="user-name">{exportItem.user?.nickname || "N/A"}</div>
                          <div className="user-email">{exportItem.user?.email || "N/A"}</div>
                          <div className="user-username">@{exportItem.user?.username || "N/A"}</div>
                        </div>
                      </div>
                    </td>
                    <td>{getStatusBadge(exportItem.status)}</td>
                    <td>{getDataTypesBadges(exportItem.data_types)}</td>
                    <td>{formatDate(exportItem.createdAt)}</td>
                    <td>
                      {exportItem.expires_at ? (
                        <span className={isExpired(exportItem.expires_at) ? "expired" : "not-expired"}>
                          {formatDate(exportItem.expires_at)}
                          {isExpired(exportItem.expires_at) && " (Expired)"}
                        </span>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td>
                      {exportItem.status === "completed" && exportItem.url ? (
                        isExpired(exportItem.expires_at) ? (
                          <span className="expired-link">Expired</span>
                        ) : (
                          <button
                            className="download-btn"
                            onClick={() => handleDownload(exportItem)}
                          >
                            Download
                          </button>
                        )
                      ) : (
                        <span className="not-available">N/A</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        {exportItem.status === "processing" && (
                          <>
                            <button
                              className="approve-btn"
                              onClick={() => handleApprove(exportItem.id)}
                            >
                              Approve
                            </button>
                            <button
                              className="reject-btn"
                              onClick={() => handleReject(exportItem.id)}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(exportItem.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button onClick={handleFirst} disabled={pagination.page === 1}>
                  First
                </button>
                <button onClick={handlePrevious} disabled={!pagination.hasPrev}>
                  Previous
                </button>
                <span className="page-info">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>
                <button onClick={handleNext} disabled={!pagination.hasNext}>
                  Next
                </button>
                <button onClick={handleLast} disabled={pagination.page === pagination.totalPages}>
                  Last
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </IncludeSideBar>
  );
};

export default DataExports;
